import type { TotalCostCellData } from './types';
import { kind } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { isCurrencyISO } from '@/constants/currencies';

import { currencyToSymbol } from '@/utilities/formatting';

const defaultConfig = {
  allowOverlay: false,
  readonly: true,
};

const getDefaultCellData = ({
  value = null,
  currencyISO = null,
}: Partial<TotalCostCellData> = {}): TotalCostCellData => ({
  kind,
  value,
  currencyISO,
});

const isCellData = (data: unknown): data is TotalCostCellData => {
  if (!isCustomCellData(data)) return false;
  return data.kind === kind;
};

type Args = Pick<Partial<TotalCostCellData>, 'currencyISO'> & {
  value?: unknown;
};

/**
 * ## getCellTemplate
 *
 * @param args: Partial SharesCell data object
 * @returns A custom SharesCell object and its data
 */
export const getCellTemplate = ({
  value = null,
  currencyISO = null,
}: Args = {}) => {
  if (typeof value === 'number' || value === null) {
    const currencySymbol: string | undefined =
      currencyToSymbol[currencyISO ?? '']?.currency.symbol;

    const copyData = `${currencySymbol ?? ''}${String(value ?? '')}`;

    const validCurrencyISO = isCurrencyISO(currencyISO) ? currencyISO : null;
    const cellData = getDefaultCellData({
      value,
      currencyISO: validCurrencyISO,
    });
    return getCustomCellTemplate({ cellData, copyData, ...defaultConfig });
  }
  return getCustomCellTemplate({
    cellData: getDefaultCellData(),
    ...defaultConfig,
  });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data: unknown) => {
  if (isCellData(data) && typeof data.value === 'number') {
    return data;
  }

  if (typeof data === 'number') {
    return getDefaultCellData({ value: data });
  }

  if (typeof data === 'string' && !Number.isNaN(Number(data))) {
    return getDefaultCellData({ value: Number(data) });
  }

  return getDefaultCellData();
};
